import React from "react";
import Moment from "react-moment";
import { Dropdown } from "react-bootstrap";
import {
	FiMoreHorizontal,
	FiCheck,
	FiX,
	FiMessageSquare,
	FiAlertTriangle,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { expStatus } from "services";
import MobileAttachmentThumb from "./MobileAttachmentThumb";
import "./MobileExpenseCard.scss";

const statusToModifier = (status) => {
	if (!status) return "unknown";
	return String(status)
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/\s+/g, "-");
};

/** API puede devolver `answered`/`paid` o `answered_at`/`paid_at` según listado. */
const pickTimestamp = (expense, keys) => {
	for (const key of keys) {
		const v = expense[key];
		if (v !== null && v !== undefined && v !== "") return v;
	}
	return null;
};

const approvalFooterLabel = (status, t) => {
	if (status === expStatus.denied) return t("expenses.mobile.card.denied");
	if (status === expStatus.canceled) return t("expenses.mobile.card.cancelled");
	return t("expenses.mobile.card.approved");
};

/**
 * Tarjeta de gasto: modo lista (estado + menú) o modo aprobación (acciones rápidas).
 */
const MobileExpenseCard = ({
	expense,
	mode,
	expense_types,
	expense_groupings,
	onOpenDetail,
	onAddPayment,
	onCancelExpense,
	onDisapproveExpense,
	onApproveQuick,
	onDenyQuick,
	onCommentModal,
	onDownloadAttachment,
}) => {
	const { t } = useTranslation();
	const employee = expense.requester;
	const fullName = employee
		? `${employee.name ?? ""} ${employee.surname ?? ""}`.trim()
		: "";

	const typeObj =
		expense.type ??
		expense_types?.find((t) => t.id === expense.type_id) ??
		null;

	const groupingRaw =
		expense.grouping ??
		expense_groupings?.find(
			(g) => g.id === (expense.grouping?.id ?? expense.grouping_id)
		);

	const maxValue = typeObj?.max_value;
	const exceeded =
		maxValue && parseFloat(expense.value) > parseFloat(maxValue);

	const statusMod = statusToModifier(expense.status);
	const hasAttachment = expense.attachments?.length > 0;

	const answeredAt = pickTimestamp(expense, ["answered", "answered_at"]);
	const paidAt = pickTimestamp(expense, ["paid", "paid_at"]);
	const approvalLabel = approvalFooterLabel(expense.status, t);

	return (
		<div className="mobile-expense-card">
			<div className="mobile-expense-card__header">
			<div className="mobile-expense-card__header-group">

				<div className="mobile-expense-card__avatar">
					{employee?.name ? employee.name.charAt(0).toUpperCase() : "?"}
					{employee?.surname
						? employee.surname.charAt(0).toUpperCase()
						: ""}
				</div>
				<div className="mobile-expense-card__header-main">
					<div className="mobile-expense-card__name-row">
						<span className="mobile-expense-card__name">{fullName || "—"}</span>
					</div>
					<div className="mobile-expense-card__meta">
						<Moment format="DD/MM/YYYY HH:mm">{expense.requested}</Moment>
					</div>
				</div>
				<div className="mobile-expense-card__header-actions">
					{mode === "list" ? (
						<>
							<span
								className={`mobile-expense-card__status mobile-expense-card__status--${statusMod}`}
							>
								{t(`expenses.statusLabels.${expense.status}`, expense.status)}
							</span>
							<Dropdown align="end">
								<Dropdown.Toggle
									variant="link"
									className="mobile-expense-card__menu-toggle"
									aria-label={t("expenses.mobile.card.moreOptions")}
								>
									<FiMoreHorizontal size={20} />
								</Dropdown.Toggle>
								<Dropdown.Menu>
									<Dropdown.Item as="button" onClick={() => onOpenDetail(expense)}>
										{expense.status !== expStatus.pending ? t("common.view") : t("common.edit")}
									</Dropdown.Item>
									{expense.status === expStatus.approved && onAddPayment && (
										<Dropdown.Item as="button" onClick={() => onAddPayment(expense)}>
											{t("expenses.addPayment")}
										</Dropdown.Item>
									)}
									{expense.status === expStatus.approved && onDisapproveExpense && (
										<Dropdown.Item as="button" onClick={() => onDisapproveExpense(expense)}>
											{t("expenses.disapproveExpense")}
										</Dropdown.Item>
									)}
									{expense.status === expStatus.pending && onCancelExpense && (
										<Dropdown.Item as="button" onClick={() => onCancelExpense(expense)}>
											{t("common.cancel")}
										</Dropdown.Item>
									)}
								</Dropdown.Menu>
							</Dropdown>
						</>
					) : (
						<div className="mobile-expense-card__approve-actions">
							<button
								type="button"
								className="mobile-expense-card__icon-btn mobile-expense-card__icon-btn--ok"
								title={t("expenses.mobile.card.approve")}
								onClick={() => onApproveQuick(expense)}
							>
								<FiCheck size={18} />
							</button>
							<button
								type="button"
								className="mobile-expense-card__icon-btn mobile-expense-card__icon-btn--no"
								title={t("expenses.mobile.card.deny")}
								onClick={() => onDenyQuick(expense)}
							>
								<FiX size={18} />
							</button>
							<button
								type="button"
								className="mobile-expense-card__icon-btn mobile-expense-card__icon-btn--comment"
								title={t("expenses.approveOrDenyWithComments")}
								onClick={() => onCommentModal(expense)}
							>
								<FiMessageSquare size={18} />
							</button>
						</div>
					)}
				</div>
			</div>
			</div>

			<button
				type="button"
				className="mobile-expense-card__body-btn"
				onClick={() => onOpenDetail(expense)}
			>
				<div className="mobile-expense-card__row">
					<span
						className={`mobile-expense-card__amount${exceeded ? " mobile-expense-card__amount--exceeded" : ""}`}
					>
						{expense.value} €
						{exceeded && (
							<FiAlertTriangle
								className="mobile-expense-card__warn"
								title={t("expenses.mobile.card.limitExceeded")}
							/>
						)}
					</span>
					{typeObj ? (
						<span
							className="mobile-expense-card__category"
							style={{
								backgroundColor: typeObj.background_color || undefined,
								...(typeObj.border_color
									? { boxShadow: `0 0 0 1px ${typeObj.border_color}` }
									: {}),
							}}
						>
							{typeObj.name ?? expense.type_name}
						</span>
					) : null}
				{groupingRaw?.name ? (
					<span
						className="mobile-expense-card__location-tag"
						style={{
							backgroundColor: groupingRaw.background_color || undefined,
							...(groupingRaw.border_color
								? { boxShadow: `0 0 0 1px ${groupingRaw.border_color}` }
								: {}),
						}}
					>
						{groupingRaw.name}
					</span>
				) : null}
				{hasAttachment && (
					<MobileAttachmentThumb
						expenseId={expense.id}
						attachment={expense.attachments[0]}
						size={38}
						count={expense.attachments.length}
						className="mobile-expense-card__thumb"
					/>
				)}
				</div>
			</button>

			{mode !== "approve" && <div className="mobile-expense-card__footer">
				<div className="mobile-expense-card__footer-col">
					<span className="mobile-expense-card__footer-label">
						{approvalLabel}:
					</span>
					<span className="mobile-expense-card__footer-value">
						{answeredAt ? (
							<Moment format="DD/MM/YYYY HH:mm">{answeredAt}</Moment>
						) : (
							"—"
						)}
					</span>
				</div>
				<div className="mobile-expense-card__footer-col mobile-expense-card__footer-col--end">
					<span className="mobile-expense-card__footer-label">{t("expenses.mobile.card.paid")}</span>
					<span className="mobile-expense-card__footer-value">
						{paidAt ? (
							<Moment format="DD/MM/YYYY HH:mm">{paidAt}</Moment>
						) : (
							"—"
						)}
					</span>
				</div>
			</div>}
		</div>
	);
};

export default MobileExpenseCard;
